import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const schema = z.object({
  name: z
    .string()
    .trim()
    .min(1, "Name is required")
    .max(80, "Max 80 characters"),
});

type FormValues = z.infer<typeof schema>;

interface TemplateSaveDialogProps {
  open: boolean;
  initialName?: string;
  mode: "create" | "rename";
  onOpenChange: (open: boolean) => void;
  onSubmit: (name: string) => void;
}

export function TemplateSaveDialog({
  open,
  initialName,
  mode,
  onOpenChange,
  onSubmit,
}: TemplateSaveDialogProps) {
  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { name: initialName ?? "" },
  });

  useEffect(() => {
    if (open) {
      form.reset({ name: initialName ?? "" });
    }
  }, [open, initialName, form]);

  const handleSubmit = form.handleSubmit((values) => {
    onSubmit(values.name.trim());
    onOpenChange(false);
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[420px]">
        <DialogHeader>
          <DialogTitle>
            {mode === "rename" ? "Save as new template" : "Save template"}
          </DialogTitle>
          <DialogDescription>
            Stored locally in your browser. Not synced.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="template-name">Name</Label>
            <Input
              id="template-name"
              autoFocus
              placeholder="e.g. Welcome email v1"
              {...form.register("name")}
            />
            {form.formState.errors.name && (
              <p className="text-sm text-destructive">
                {form.formState.errors.name.message}
              </p>
            )}
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button type="submit">Save</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
